/**
 * Analytics page — per docs/specs/analytics.md
 *
 * Three tabs: Overview (DAU/WAU/MAU, platform split, heatmap, adoption,
 * geography, events + §4.7 event inspector), Funnels (onboarding + activation
 * + §5.3 per-feature funnels), Cohorts (retention heatmap + §6.2 paid-vs-free
 * retention + §6.3 time-to-first-paid).
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v1
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import {
    downloadAnalyticsCsv,
    useAnalyticsKpis,
    useCohorts,
    useConversionTime,
    useDauChart,
    useFeatureAdoption,
    useFunnel,
    useGeography,
    useHeatmap,
    usePaidVsFreeRetention,
    usePerFeatureFunnels,
    usePlatformSplit
} from '../../lib/api';
import { apiGet, getHttpErrorMessage } from '../../lib/http';
import { Chip } from '../components/chip';
import { downloadCsv, exportCsv } from '../../lib/csv';
import { useToast } from '../components/toast';
import type { AnalyticsEvent, PerFeatureFunnelSteps, Tone } from '../../lib/types';
import { AnalyticsEventInspector } from './analytics-event-inspector';
import {
    ANALYTICS_DATE_RANGES,
    ANALYTICS_TABS,
    DEFAULT_ANALYTICS_DATE_RANGE,
    DEFAULT_ANALYTICS_TAB,
    parseAnalyticsDateRange,
    parseAnalyticsTab,
    parseLiveTail,
    type AnalyticsDateRange,
    analyticsDateRangeDays,
    normalizeEventTypePrefix as normalizeAnalyticsEventTypePrefix
} from './analytics-filters';

const fmtMoney = (n: number) => `$${(n / 1000).toFixed(1)}k`;
const LIVE_EVENT_POLL_MS = 7_000;
const RECENT_EVENT_LIMIT = 50;
type CsvExportPayload = {
    filename: string;
    rows: Array<Record<string, unknown>>;
};

export function AnalyticsPage() {
    const [params, setParams] = useSearchParams();
    const { toast } = useToast();
    const tab = parseAnalyticsTab(params.get('tab'));
    const dateRange = parseAnalyticsDateRange(params.get('days'));
    const eventTypePrefix = normalizeAnalyticsEventTypePrefix(params.get('event_type_prefix'));
    const liveTail = parseLiveTail(params.get('live_tail'));
    const [exportPayload, setExportPayload] = useState<CsvExportPayload>({
        filename: `analytics-overview-${new Date().toISOString().slice(0, 10)}.csv`,
        rows: []
    });

    useEffect(() => {
        setExportPayload({
            filename: `analytics-${tab}-${dateRange}-${new Date().toISOString().slice(0, 10)}.csv`,
            rows: []
        });
    }, [tab, dateRange]);

    const updateParam = (key: string, value: string | null) => {
        const next = new URLSearchParams(params);
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
        setParams(next, { replace: true });
    };

    const handleExport = async () => {
        if (tab === 'overview') {
            const workerParams = new URLSearchParams({
                view: 'events',
                days: String(analyticsDateRangeDays(dateRange))
            });
            if (eventTypePrefix) workerParams.set('event_type_prefix', eventTypePrefix);

            try {
                const csv = await downloadAnalyticsCsv(workerParams);
                downloadCsv(
                    `analytics-overview-${dateRange}-${new Date().toISOString().slice(0, 10)}`,
                    csv
                );
                toast('Exported overview stream from Worker');
                return;
            } catch (error) {
                if (!exportPayload.rows.length) {
                    toast(getHttpErrorMessage(error, 'Failed to export analytics overview'), {
                        kind: 'danger'
                    });
                    return;
                }
                toast('Worker export unavailable, using the current stream snapshot');
            }
        }

        if (!exportPayload.rows.length) {
            toast('No rows available for export');
            return;
        }
        exportCsv(exportPayload.filename, exportPayload.rows);
        toast(`Exported ${tab} view`);
    };

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Analytics</h1>
                    <p>Product behaviour: usage, funnels, cohorts.</p>
                </div>
                <div className="page-actions">
                    <select
                        className="select"
                        value={dateRange}
                        aria-label="Range"
                        onChange={(event) => {
                            const next = event.target.value as AnalyticsDateRange;
                            updateParam(
                                'days',
                                next === DEFAULT_ANALYTICS_DATE_RANGE ? null : next
                            );
                        }}
                    >
                        {ANALYTICS_DATE_RANGES.map((range) => (
                            <option key={range} value={range}>
                                Last {range.replace('d', '')} days
                            </option>
                        ))}
                    </select>
                    <button className="btn btn-ghost" onClick={handleExport}>
                        Export CSV
                    </button>
                </div>
            </header>

            <section className="alert-bar">
                <div className="alert" data-severity="info">
                    <span className="msg">
                        The event stream is polling-backed every {LIVE_EVENT_POLL_MS / 1000}s when
                        live tail is enabled. Session, geography, and cohort telemetry stay
                        unavailable until the session pipeline lands.
                    </span>
                    <span className="meta">polling</span>
                </div>
            </section>

            <div className="segment-tabs" role="tablist" aria-label="Analytics tab">
                {ANALYTICS_TABS.map((t) => (
                    <button
                        key={t}
                        role="tab"
                        aria-selected={tab === t}
                        onClick={() => updateParam('tab', t === DEFAULT_ANALYTICS_TAB ? null : t)}
                        className="segment-tab"
                    >
                        {t[0].toUpperCase() + t.slice(1)}
                    </button>
                ))}
            </div>

            {tab === 'overview' && (
                <OverviewTab
                    dateRange={dateRange}
                    eventTypePrefix={eventTypePrefix}
                    liveTail={liveTail}
                    onEventTypePrefixChange={(value) =>
                        updateParam(
                            'event_type_prefix',
                            normalizeAnalyticsEventTypePrefix(value) || null
                        )
                    }
                    onLiveTailChange={(value) => updateParam('live_tail', value ? '1' : '0')}
                    onExport={setExportPayload}
                />
            )}
            {tab === 'funnels' && <FunnelsTab onExport={setExportPayload} />}
            {tab === 'cohorts' && <CohortsTab onExport={setExportPayload} />}
        </div>
    );
}

function OverviewTab({
    dateRange,
    eventTypePrefix,
    liveTail,
    onEventTypePrefixChange,
    onLiveTailChange,
    onExport
}: {
    dateRange: AnalyticsDateRange;
    eventTypePrefix: string;
    liveTail: boolean;
    onEventTypePrefixChange: (value: string) => void;
    onLiveTailChange: (value: boolean) => void;
    onExport: (payload: CsvExportPayload) => void;
}) {
    const days = analyticsDateRangeDays(dateRange);
    const kpis = useAnalyticsKpis(days);
    const dau = useDauChart(days);
    const platform = usePlatformSplit(days);
    const heatmap = useHeatmap(days);
    const adoption = useFeatureAdoption(days);
    const geo = useGeography(days);
    const events = useRecentAnalyticsEvents({ dateRange, eventTypePrefix, liveTail });
    const [inspect, setInspect] = useState<AnalyticsEvent | null>(null);

    useEffect(() => {
        onExport({
            filename: `analytics-events-${dateRange}-${eventTypePrefix || 'all'}-${new Date().toISOString().slice(0, 10)}.csv`,
            rows: (events.data ?? []).map((e) => ({
                ...e,
                properties: JSON.stringify(e.properties ?? {})
            }))
        });
    }, [onExport, dateRange, eventTypePrefix, events.data]);

    const formatUnavailable = (
        value: number | null | undefined,
        formatter: (n: number) => string
    ) => {
        if (value === null) return 'Unavailable';
        if (value === undefined) return '—';
        return formatter(value);
    };

    return (
        <>
            <section className="kpi-grid kpi-grid-8" aria-label="Usage KPIs">
                <KpiMini label="DAU" value={kpis.data?.dau.toLocaleString() ?? '—'} />
                <KpiMini label="WAU" value={kpis.data?.wau.toLocaleString() ?? '—'} />
                <KpiMini label="MAU" value={kpis.data?.mau.toLocaleString() ?? '—'} />
                <KpiMini
                    label="Stickiness"
                    value={kpis.data ? `${kpis.data.stickiness.toFixed(1)}%` : '—'}
                />
                <KpiMini
                    label="Avg session"
                    value={formatUnavailable(kpis.data?.avg_session_min, (n) => `${n}m`)}
                />
                <KpiMini
                    label="Sessions / user"
                    value={formatUnavailable(kpis.data?.sessions_per_user, (n) => n.toFixed(1))}
                />
                <KpiMini
                    label="Power users"
                    value={formatUnavailable(kpis.data?.power_users, (n) => n.toLocaleString())}
                />
                <KpiMini
                    label="Dormant & at risk"
                    value={formatUnavailable(kpis.data?.dormant, (n) => n.toLocaleString())}
                />
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">DAU + stickiness · Last {dateRange}</h2>
                </header>
                <div className="card-body" style={{ height: 260 }}>
                    {dau.data && dau.data.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={dau.data}>
                                <defs>
                                    <linearGradient id="dauG" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.45} />
                                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.04)" />
                                <XAxis
                                    dataKey="date"
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
                                    minTickGap={32}
                                />
                                <YAxis
                                    yAxisId="dau"
                                    tickLine={false}
                                    axisLine={false}
                                    width={32}
                                    tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
                                />
                                <YAxis
                                    yAxisId="stk"
                                    orientation="right"
                                    tickLine={false}
                                    axisLine={false}
                                    width={28}
                                    tick={{ fontSize: 10, fill: 'rgba(168,85,247,0.6)' }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: '#12121a',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: 8,
                                        fontSize: 12
                                    }}
                                    labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
                                />
                                <Area
                                    yAxisId="dau"
                                    type="monotone"
                                    dataKey="dau"
                                    stroke="#3b82f6"
                                    strokeWidth={2}
                                    fill="url(#dauG)"
                                />
                                <Line
                                    yAxisId="stk"
                                    type="monotone"
                                    dataKey="stickiness"
                                    stroke="#a855f7"
                                    strokeWidth={1.5}
                                    strokeDasharray="3 3"
                                    dot={false}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="empty-state" style={{ height: '100%' }}>
                            <div className="title">No DAU history available yet</div>
                        </div>
                    )}
                </div>
            </section>

            <div className="grid-split">
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Platform split</h2>
                    </header>
                    <div className="card-body tight">
                        {platform.data && platform.data.length > 0 ? (
                            platform.data.map((p) => (
                                <div
                                    key={p.os}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr auto auto',
                                        gap: 12,
                                        padding: '10px 16px',
                                        borderBottom: '1px solid var(--border)',
                                        fontSize: 12
                                    }}
                                >
                                    <span style={{ color: 'var(--text-primary)' }}>{p.os}</span>
                                    <span className="mono text-muted">
                                        {p.count.toLocaleString()}
                                    </span>
                                    <span className="text-muted">{p.pct.toFixed(1)}%</span>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="title">Platform telemetry unavailable</div>
                            </div>
                        )}
                    </div>
                </section>

                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Top geography</h2>
                    </header>
                    <div className="card-body tight">
                        {geo.data && geo.data.length > 0 ? (
                            geo.data.map((g) => (
                                <div
                                    key={g.country}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr auto auto',
                                        gap: 12,
                                        padding: '10px 16px',
                                        borderBottom: '1px solid var(--border)',
                                        fontSize: 12
                                    }}
                                >
                                    <span style={{ color: 'var(--text-primary)' }}>
                                        {g.country}
                                    </span>
                                    <span className="mono text-muted">
                                        {g.mau.toLocaleString()} MAU
                                    </span>
                                    <span className="mono">{fmtMoney(g.revenue)}</span>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="title">Geography telemetry unavailable</div>
                                <div className="desc">
                                    No country source is wired yet, so this stays explicitly empty.
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Time-of-day heatmap · UTC</h2>
                </header>
                <div className="card-body">
                    {heatmap.data && heatmap.data.length > 0 ? (
                        <Heatmap cells={heatmap.data} />
                    ) : (
                        <div className="empty-state">
                            <div className="title">Session heatmap unavailable</div>
                            <div className="desc">
                                The worker does not have a session source yet, so this stays
                                explicitly empty.
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Feature adoption · % of MAU</h2>
                </header>
                <div className="card-body tight">
                    {adoption.data && adoption.data.length > 0 ? (
                        adoption.data.map((f) => (
                            <div
                                key={f.feature}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '160px 1fr auto auto',
                                    gap: 12,
                                    alignItems: 'center',
                                    padding: '8px 16px',
                                    borderBottom: '1px solid var(--border)',
                                    fontSize: 12
                                }}
                            >
                                <span style={{ color: 'var(--text-primary)' }}>{f.feature}</span>
                                <div className="progress-track">
                                    <div
                                        className="progress-fill"
                                        data-tone={f.adoption_pct < 5 ? 'danger' : undefined}
                                        style={{ width: `${Math.min(100, f.adoption_pct)}%` }}
                                    />
                                </div>
                                <span className="mono text-muted">
                                    {f.mau_count.toLocaleString()}
                                </span>
                                <span
                                    className="mono"
                                    style={{
                                        color:
                                            f.adoption_pct < 5
                                                ? 'var(--danger)'
                                                : 'var(--text-primary)',
                                        minWidth: 50,
                                        textAlign: 'right'
                                    }}
                                >
                                    {f.adoption_pct.toFixed(1)}%
                                </span>
                            </div>
                        ))
                    ) : (
                        <div className="empty-state">
                            <div className="title">Feature adoption telemetry unavailable</div>
                        </div>
                    )}
                </div>
            </section>

            <section className="card">
                <header
                    className="card-header"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: 12,
                        alignItems: 'center'
                    }}
                >
                    <div>
                        <h2 className="card-title">Recent events</h2>
                        <span className="text-muted" style={{ fontSize: 11 }}>
                            Last {dateRange} · click a row to inspect payload ·{' '}
                            {eventTypePrefix ? `filter: ${eventTypePrefix}` : 'no filter'}
                        </span>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                            justifySelf: 'end'
                        }}
                    >
                        <input
                            className="form-input"
                            value={eventTypePrefix}
                            onChange={(event) => onEventTypePrefixChange(event.target.value)}
                            placeholder="Filter by event prefix"
                            style={{ width: 170, fontSize: 11, height: 28 }}
                            aria-label="Event type prefix"
                        />
                        <label
                            style={{
                                fontSize: 11,
                                color: 'var(--text-muted)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={liveTail}
                                onChange={(event) => onLiveTailChange(event.target.checked)}
                            />
                            Live tail
                        </label>
                    </div>
                </header>
                <div className="card-body tight">
                    {events.data && events.data.length > 0 ? (
                        events.data.map((e) => (
                            <button
                                key={e.id}
                                type="button"
                                onClick={() => setInspect(e)}
                                className="analytics-event-row"
                            >
                                <span className="mono text-muted">{e.ts}</span>
                                <span className="mono" style={{ color: 'var(--primary)' }}>
                                    {e.event_type}
                                </span>
                                <span className="text-muted">{e.user_email ?? 'anon'}</span>
                                <span className="text-muted mono">
                                    {e.properties &&
                                        Object.entries(e.properties)
                                            .map(([k, v]) => `${k}=${v}`)
                                            .join(' · ')}
                                </span>
                            </button>
                        ))
                    ) : (
                        <div className="empty-state">
                            <div className="title">No analytics events available</div>
                        </div>
                    )}
                </div>
            </section>

            <AnalyticsEventInspector event={inspect} onClose={() => setInspect(null)} />
        </>
    );
}

function FunnelsTab({ onExport }: { onExport: (payload: CsvExportPayload) => void }) {
    const onboarding = useFunnel('onboarding');
    const activation = useFunnel('activation');
    const perFeature = usePerFeatureFunnels();

    useEffect(() => {
        const rows = [
            ...(onboarding.data ?? []).map((step) => ({
                view: 'onboarding',
                step: step.step,
                count: step.count,
                pct: step.pct,
                dropoff: step.dropoff
            })),
            ...(activation.data ?? []).map((step) => ({
                view: 'activation',
                step: step.step,
                count: step.count,
                pct: step.pct,
                dropoff: step.dropoff
            })),
            ...(perFeature.data ?? []).flatMap((feature) =>
                feature.steps.map((step) => ({
                    view: 'feature',
                    feature: feature.feature,
                    step: step.step,
                    count: step.count,
                    pct: step.pct,
                    retention_pct: feature.retention_pct
                }))
            )
        ];

        onExport({
            filename: `analytics-funnels-${new Date().toISOString().slice(0, 10)}.csv`,
            rows
        });
    }, [activation.data, onboarding.data, onExport, perFeature.data]);

    return (
        <>
            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Onboarding funnel</h2>
                </header>
                <div className="card-body tight">
                    {onboarding.data && onboarding.data.length > 0 ? (
                        onboarding.data.map((s, _i, arr) => (
                            <FunnelRow key={s.step} step={s} max={arr[0].count} />
                        ))
                    ) : (
                        <div className="empty-state">
                            <div className="title">Onboarding funnel unavailable</div>
                        </div>
                    )}
                </div>
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Activation funnel · signup → paid</h2>
                </header>
                <div className="card-body tight">
                    {activation.data && activation.data.length > 0 ? (
                        activation.data.map((s, _i, arr) => (
                            <FunnelRow key={s.step} step={s} max={arr[0].count} />
                        ))
                    ) : (
                        <div className="empty-state">
                            <div className="title">Activation funnel unavailable</div>
                        </div>
                    )}
                </div>
            </section>

            <section>
                <header className="page-subheader">
                    <h2 className="section-title">Per-feature adoption funnels</h2>
                    <p className="section-sub">
                        Discovered → tried → used 3× → regular. Retention chip compares regulars
                        against users who discovered the feature.
                    </p>
                </header>
                <div className="per-feature-grid">
                    {perFeature.data && perFeature.data.length > 0 ? (
                        perFeature.data.map((f) => (
                            <PerFeatureFunnelCard key={f.feature} data={f} />
                        ))
                    ) : (
                        <div className="empty-state">
                            <div className="title">Per-feature funnel telemetry unavailable</div>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}

function retentionTone(pct: number): Tone {
    if (pct >= 40) return 'success';
    if (pct >= 25) return 'info';
    if (pct >= 12) return 'warning';
    return 'danger';
}

function PerFeatureFunnelCard({ data }: { data: PerFeatureFunnelSteps }) {
    const max = data.steps[0]?.count ?? 1;
    return (
        <section className="card">
            <header className="card-header">
                <h3 className="card-title">{data.feature}</h3>
                <Chip tone={retentionTone(data.retention_pct)} dot>
                    {data.retention_pct.toFixed(1)}% regular / discovered
                </Chip>
            </header>
            <div className="card-body tight">
                {data.steps.map((s) => (
                    <div key={s.step} className="per-feature-funnel-row">
                        <span style={{ color: 'var(--text-primary)' }}>{s.step}</span>
                        <div className="progress-track">
                            <div
                                className="progress-fill"
                                style={{ width: `${(s.count / max) * 100}%` }}
                            />
                        </div>
                        <span className="mono text-muted">{s.count.toLocaleString()}</span>
                        <span className="mono" style={{ minWidth: 48, textAlign: 'right' }}>
                            {s.pct.toFixed(1)}%
                        </span>
                    </div>
                ))}
            </div>
        </section>
    );
}

function FunnelRow({
    step,
    max
}: {
    step: { step: string; count: number; pct: number; dropoff: number };
    max: number;
}) {
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '200px 1fr auto auto',
                gap: 12,
                alignItems: 'center',
                padding: '10px 16px',
                borderBottom: '1px solid var(--border)',
                fontSize: 12
            }}
        >
            <span style={{ color: 'var(--text-primary)' }}>{step.step}</span>
            <div className="progress-track">
                <div className="progress-fill" style={{ width: `${(step.count / max) * 100}%` }} />
            </div>
            <span className="mono" style={{ minWidth: 60, textAlign: 'right' }}>
                {step.count.toLocaleString()}
            </span>
            <span className="text-muted mono" style={{ minWidth: 80, textAlign: 'right' }}>
                {step.pct.toFixed(1)}%
                {step.dropoff > 0 && (
                    <span style={{ color: 'var(--danger)', marginLeft: 6 }}>
                        −{step.dropoff.toLocaleString()}
                    </span>
                )}
            </span>
        </div>
    );
}

function CohortsTab({ onExport }: { onExport: (payload: CsvExportPayload) => void }) {
    const cohorts = useCohorts();
    const paidVsFree = usePaidVsFreeRetention();
    const conversion = useConversionTime();
    const cols = ['W0', 'W1', 'W2', 'W4', 'W8', 'W12', 'W24'];
    const hasConversionBuckets =
        conversion.data?.buckets.some((bucket) => bucket.count > 0) ?? false;

    useEffect(() => {
        const rows = [
            ...(cohorts.data ?? []).flatMap((row) =>
                cols.map((bucket) => ({
                    view: 'cohort',
                    cohort: row.cohort,
                    size: row.size,
                    bucket,
                    retention: row.retention[bucket] ?? null
                }))
            ),
            ...(paidVsFree.data ?? []).flatMap((row) => [
                {
                    view: 'paid-vs-free',
                    cohort: row.cohort,
                    tier: 'paid',
                    w8: row.paid_w8
                },
                {
                    view: 'paid-vs-free',
                    cohort: row.cohort,
                    tier: 'free',
                    w8: row.free_w8
                }
            ]),
            ...(conversion.data?.buckets ?? []).map((bucket) => ({
                view: 'conversion-time',
                bucket: bucket.bucket,
                label: bucket.label,
                count: bucket.count,
                pct: bucket.pct,
                median_days: conversion.data?.median_days ?? 0
            }))
        ];

        onExport({
            filename: `analytics-cohorts-${new Date().toISOString().slice(0, 10)}.csv`,
            rows
        });
    }, [cohorts.data, conversion.data, onExport, paidVsFree.data]);

    return (
        <>
            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Paying vs Free · W8 retention</h2>
                    <span className="text-muted" style={{ fontSize: 11 }}>
                        Last 5 cohort months
                    </span>
                </header>
                <div className="card-body">
                    {paidVsFree.data && paidVsFree.data.length > 0 ? (
                        <div className="paid-vs-free-grid">
                            {paidVsFree.data.map((row) => (
                                <div key={row.cohort} className="paid-vs-free-card">
                                    <div className="paid-vs-free-label mono">{row.cohort}</div>
                                    <div className="paid-vs-free-row">
                                        <span className="paid-vs-free-tag" data-tone="paid">
                                            Paid
                                        </span>
                                        <div className="paid-vs-free-bar">
                                            <div
                                                className="paid-vs-free-bar-fill"
                                                data-tone="paid"
                                                style={{ width: `${row.paid_w8}%` }}
                                            />
                                        </div>
                                        <span
                                            className="mono"
                                            style={{ color: 'var(--success)', minWidth: 36 }}
                                        >
                                            {row.paid_w8}%
                                        </span>
                                    </div>
                                    <div className="paid-vs-free-row">
                                        <span className="paid-vs-free-tag" data-tone="free">
                                            Free
                                        </span>
                                        <div className="paid-vs-free-bar">
                                            <div
                                                className="paid-vs-free-bar-fill"
                                                data-tone="free"
                                                style={{ width: `${row.free_w8}%` }}
                                            />
                                        </div>
                                        <span
                                            className="mono"
                                            style={{ color: 'var(--text-muted)', minWidth: 36 }}
                                        >
                                            {row.free_w8}%
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="title">Paid-vs-free retention unavailable</div>
                        </div>
                    )}
                </div>
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Retention heatmap · last 8 weekly cohorts</h2>
                </header>
                <div className="card-body">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Cohort</th>
                                <th className="numeric">Size</th>
                                {cols.map((c) => (
                                    <th key={c} className="numeric">
                                        {c}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {cohorts.data && cohorts.data.length > 0 ? (
                                cohorts.data.map((c) => (
                                    <tr key={c.cohort}>
                                        <td className="mono">{c.cohort}</td>
                                        <td className="numeric text-muted">{c.size}</td>
                                        {cols.map((k) => (
                                            <td
                                                key={k}
                                                className="numeric"
                                                style={{
                                                    background:
                                                        c.retention[k] !== null &&
                                                        c.retention[k] !== undefined
                                                            ? `rgba(59, 130, 246, ${(c.retention[k]! / 100) * 0.6})`
                                                            : 'transparent',
                                                    color:
                                                        c.retention[k] !== null &&
                                                        c.retention[k] !== undefined
                                                            ? 'var(--text-primary)'
                                                            : 'var(--text-dim)'
                                                }}
                                            >
                                                {c.retention[k] === null ||
                                                c.retention[k] === undefined
                                                    ? '—'
                                                    : `${c.retention[k]!.toFixed(0)}%`}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={9} className="empty-state">
                                        No cohort retention grid is available yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Time-to-first-paid distribution</h2>
                    {conversion.data && hasConversionBuckets && (
                        <Chip tone="info" dot>
                            Median: {conversion.data.median_days}d
                        </Chip>
                    )}
                </header>
                <div className="card-body" style={{ height: 240 }}>
                    {conversion.data && hasConversionBuckets ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={conversion.data.buckets}
                                margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
                            >
                                <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.04)" />
                                <XAxis
                                    dataKey="label"
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
                                />
                                <YAxis
                                    tickLine={false}
                                    axisLine={false}
                                    width={28}
                                    tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
                                />
                                <Tooltip
                                    cursor={{ fill: 'rgba(59,130,246,0.08)' }}
                                    contentStyle={{
                                        background: '#12121a',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: 8,
                                        fontSize: 12
                                    }}
                                    labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
                                    formatter={(v, _n, entry) => {
                                        const row = entry?.payload as
                                            | { count: number; pct: number }
                                            | undefined;
                                        return [
                                            `${v} users · ${row?.pct.toFixed(1) ?? '0.0'}%`,
                                            'Conversions'
                                        ];
                                    }}
                                />
                                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                    {conversion.data.buckets.map((b) => (
                                        <Cell
                                            key={b.bucket}
                                            fill={
                                                b.bucket === 'same_day' || b.bucket === '7d'
                                                    ? '#34d399'
                                                    : b.bucket === '14d' || b.bucket === '30d'
                                                      ? '#3b82f6'
                                                      : b.bucket === '60d' || b.bucket === '90d'
                                                        ? '#a855f7'
                                                        : '#f87171'
                                            }
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="empty-state" style={{ height: '100%' }}>
                            <div className="title">Conversion-time telemetry unavailable</div>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}

function useRecentAnalyticsEvents({
    dateRange,
    eventTypePrefix,
    liveTail
}: {
    dateRange: AnalyticsDateRange;
    eventTypePrefix: string;
    liveTail: boolean;
}) {
    return useQuery<AnalyticsEvent[]>({
        queryKey: [
            'admin',
            'analytics',
            'events',
            dateRange,
            eventTypePrefix,
            liveTail ? 'live' : 'static'
        ],
        queryFn: () => {
            const params = new URLSearchParams();
            params.set('days', dateRange.replace('d', ''));
            params.set('limit', String(RECENT_EVENT_LIMIT));
            const prefix = eventTypePrefix.trim().toLowerCase();
            if (prefix) params.set('event_type_prefix', prefix);
            const query = params.toString();
            return apiGet<AnalyticsEvent[]>(
                `/api/admin/analytics/events${query ? `?${query}` : ''}`
            );
        },
        refetchInterval: liveTail ? LIVE_EVENT_POLL_MS : false,
        staleTime: 5_000
    });
}

function Heatmap({ cells }: { cells: { day: number; hour: number; sessions: number }[] }) {
    if (cells.length === 0) return null;
    const max = Math.max(...cells.map((c) => c.sessions));
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '36px repeat(24, 1fr)',
                gap: 2,
                fontSize: 9,
                color: 'var(--text-muted)'
            }}
        >
            <span />
            {Array.from({ length: 24 }, (_, h) => (
                <span key={h} style={{ textAlign: 'center' }}>
                    {h % 4 === 0 ? h : ''}
                </span>
            ))}
            {days.map((d, di) => (
                <div key={d} style={{ display: 'contents' }}>
                    <span style={{ paddingTop: 4 }}>{d}</span>
                    {Array.from({ length: 24 }, (_, h) => {
                        const cell = cells.find((c) => c.day === di && c.hour === h);
                        const opacity = cell ? cell.sessions / max : 0;
                        return (
                            <div
                                key={h}
                                title={cell ? `${d} ${h}:00 · ${cell.sessions} sessions` : ''}
                                style={{
                                    aspectRatio: '1 / 1',
                                    borderRadius: 2,
                                    background: `rgba(59, 130, 246, ${opacity})`,
                                    border: '1px solid rgba(255,255,255,0.02)'
                                }}
                            />
                        );
                    })}
                </div>
            ))}
        </div>
    );
}

function KpiMini({ label, value }: { label: string; value: string }) {
    return (
        <div className="kpi-tile">
            <span className="label">{label}</span>
            <span className="value">{value}</span>
        </div>
    );
}
